import { useEffect, useState } from "react";
import "./HeroCarousel.less";

/** Where the cover-crop is anchored. Maps directly to CSS object-position. */
export type HeaderFocus = "center" | "top" | "bottom";

export type HeaderImage = {
  basename: string;
  width: number;
  height: number;
  /** Crop anchor for the cover fit. Always present in index.json; defaults to "center". */
  focus?: HeaderFocus;
};

type HeaderIndex = {
  images: HeaderImage[];
};

type HeroCarouselProps = {
  folder: string;
};

const WIDTHS = [1280, 1920, 2560] as const;
const INTERVAL_MS = 4000;
const FALLBACK_FOLDER = "main";

/** Hardcoded first entry from /header/main/index.json — renders before the fetch resolves. */
export const FIRST_MAIN_IMAGE: HeaderImage = {
  basename: "balkan-campers-OYwK6AZ8_Bw-unsplash",
  width: 2560,
  height: 1707,
  focus: "bottom",
};

function srcSet(folder: string, img: HeaderImage, ext: string): string {
  return WIDTHS.map((w) => `/header/${folder}/${img.basename}-${w}.${ext} ${w}w`).join(", ");
}

async function loadIndex(folder: string): Promise<HeaderImage[]> {
  const res = await fetch(`/header/${folder}/index.json`);
  if (!res.ok) throw new Error(`No header index for "${folder}" (${res.status})`);
  const data = (await res.json()) as HeaderIndex;
  return data.images ?? [];
}

export function HeroCarousel({ folder }: HeroCarouselProps) {
  const [resolvedFolder, setResolvedFolder] = useState(folder);
  const [images, setImages] = useState<HeaderImage[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const fallbackImages = folder === FALLBACK_FOLDER ? [FIRST_MAIN_IMAGE] : [];
  const displayImages = images.length > 0 ? images : fallbackImages;
  const displayFolder = images.length > 0 ? resolvedFolder : FALLBACK_FOLDER;

  useEffect(() => {
    let ignore = false;

    setActiveIndex(0);
    setImages([]);

    (async () => {
      let resolved = folder;
      let loaded: HeaderImage[] = [];
      try {
        loaded = await loadIndex(folder);
      } catch {
        // Unknown / empty folder — fall back to the main carousel.
        resolved = FALLBACK_FOLDER;
        try {
          loaded = await loadIndex(FALLBACK_FOLDER);
        } catch {
          loaded = [];
        }
      }
      if (ignore) return;
      setResolvedFolder(resolved);
      setImages(loaded);
    })();

    return () => {
      ignore = true;
    };
  }, [folder]);

  useEffect(() => {
    if (images.length <= 1) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, INTERVAL_MS);

    return () => clearInterval(id);
  }, [images.length]);

  return (
    <div className="hero-carousel" data-testid="hero-carousel">
      {displayImages.map((img, i) => (
        <picture
          key={img.basename}
          className={`hero-carousel__slide${i === activeIndex ? " hero-carousel__slide--active" : ""}`}
        >
          <source type="image/avif" srcSet={srcSet(displayFolder, img, "avif")} sizes="100vw" />
          <source type="image/webp" srcSet={srcSet(displayFolder, img, "webp")} sizes="100vw" />
          <img
            src={`/header/${displayFolder}/${img.basename}-1920.jpg`}
            srcSet={srcSet(displayFolder, img, "jpg")}
            sizes="100vw"
            alt=""
            width={img.width}
            height={img.height}
            style={{ objectPosition: img.focus ?? "center" }}
            {...(i === 0
              ? { fetchPriority: "high" }
              : { loading: "lazy" as const })}
          />
        </picture>
      ))}
    </div>
  );
}
